import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Briefcase, FileText, MoreHorizontal, Users, CalendarCheck2 } from "lucide-react";
import { useAuth } from "../data/authStore";
import { useTodaySignal } from "../hooks/useTodaySignal";

/* ═══════════════════════════════════════════════════════════════════
   MobileBottomNav — global thumb-zone navigation (mobile only)
   --------------------------------------------------------------------
   IA refactor 2026-02-20 — exactly 5 calm thumb-zone tabs aligned to
   the desktop sidebar's primary surfaces:
     Today · Deals · Mission · Team · More
   The "More" tab opens the desktop sidebar drawer via custom event so
   Documents / Contacts / Notifications / Settings remain one tap away
   without overflowing the bottom row.
   ─────────────────────────────────────────────────────────────────── */

const TABS = [
  { id: "today",   label: "Flow",   Icon: CalendarCheck2, to: "/today"           },
  { id: "deals",   label: "Deals",   Icon: Briefcase,      to: "/deals"           },
  { id: "docs",    label: "Docs",    Icon: FileText,       to: "/documents"       },
  { id: "people",  label: "Contacts",  Icon: Users,          to: "/contacts"        },
  { id: "more",    label: "More",    Icon: MoreHorizontal, to: null               },
];

const PUBLIC_ROUTES = [
  "/", "/login", "/register", "/forgot-password", "/reset-password",
  "/verify-email", "/pricing", "/pipeline-audit", "/about",
  "/legal", "/legal/terms",
  "/legal/privacy", "/legal/cookies",
  "/solicitor-portal",
];

const isPublic = (pathname) => PUBLIC_ROUTES.some((p) =>
  p === pathname || (p !== "/" && pathname.startsWith(p))
);

const isActiveTab = (tab, pathname) => {
  if (!tab.to) return false;
  if (tab.id === "deals")   return pathname.startsWith("/deals") || pathname.startsWith("/deal-rooms");
  if (tab.id === "docs")    return pathname.startsWith("/documents");
  if (tab.id === "people")  return pathname.startsWith("/contacts") || pathname.startsWith("/team");
  if (tab.id === "today")   return pathname === "/today" || pathname === "/dashboard" || pathname.startsWith("/flow");
  return false;
};

export const MobileBottomNav = () => {
  const location = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();
  const [pressed, setPressed] = useState(null);
  const { hasPending, shouldPulse } = useTodaySignal(!!user);

  if (isPublic(location.pathname)) return null;
  if (!user) return null;

  const onTab = (tab) => {
    setPressed(tab.id);
    setTimeout(() => setPressed(null), 200);
    if (tab.id === "more") {
      window.dispatchEvent(new CustomEvent("cf:sidebar:toggle"));
      return;
    }
    if (tab.to) nav(tab.to);
  };

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-50"
      style={{
        background: "rgba(7,9,26,0.92)",
        borderTop: "1px solid rgba(99,102,241,0.18)",
        backdropFilter: "blur(18px) saturate(140%)",
        WebkitBackdropFilter: "blur(18px) saturate(140%)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px))",
      }}
      data-testid="mobile-bottom-nav"
    >
      <ul className="flex items-stretch h-[64px] px-1">
        {TABS.map((tab) => {
          const active = isActiveTab(tab, location.pathname);
          const isPressed = pressed === tab.id;
          const Icon = tab.Icon;
          return (
            <li key={tab.id} className="flex-1">
              <button
                type="button"
                onClick={() => onTab(tab)}
                aria-current={active ? "page" : undefined}
                className="relative w-full h-full min-h-[44px] flex flex-col items-center justify-center gap-0.5 transition-transform"
                style={{ transform: isPressed ? "scale(0.94)" : "scale(1)" }}
                data-testid={`mobile-nav-${tab.id}`}
              >
                <span
                  className="relative w-11 h-7 rounded-full flex items-center justify-center transition-all"
                  style={
                    active
                      ? {
                          background: "linear-gradient(135deg, rgba(139,92,246,0.45), rgba(99,102,241,0.30))",
                          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 6px 16px -8px rgba(139,92,246,0.55)",
                        }
                      : { background: "transparent" }
                  }
                >
                  <Icon
                    size={17}
                    strokeWidth={active ? 2.4 : 1.8}
                    color={active ? "#FFFFFF" : "#94A3B8"}
                  />
                  {tab.id === "today" && hasPending && (
                    <span
                      aria-hidden
                      data-testid="mobile-nav-today-signal"
                      data-cf-today-pulse={shouldPulse ? "true" : "false"}
                      className="absolute"
                      style={{
                        top: 4, right: 6, width: 6, height: 6,
                        borderRadius: 9999,
                        background: "rgba(165,180,252,0.95)",
                        boxShadow: "0 0 0 1.5px rgba(7,9,26,0.92), 0 0 8px rgba(139,92,246,0.55)",
                      }}
                    />
                  )}
                </span>
                <span
                  className="text-[9.5px] font-bold tracking-[0.04em] mt-0.5"
                  style={{ color: active ? "#FFFFFF" : "#64748B" }}
                >
                  {tab.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

/* Spacer to add to the bottom of any page content so it isn't hidden by the
 * fixed nav. Safe-area-aware; renders only on mobile. */
export const MobileBottomNavSpacer = () => (
  <div
    className="md:hidden"
    aria-hidden="true"
    style={{ height: "calc(64px + env(safe-area-inset-bottom, 0px))" }}
  />
);

/* Route-aware global spacer — drops a thumb-zone safe gap into the
 * document flow only on routes that actually mount the MobileBottomNav.
 * Mounted once in App.js so individual pages don't need to remember. */
export const GlobalMobileBottomSpacer = () => {
  const location = useLocation();
  if (isPublic(location.pathname)) return null;
  // Deal Room already manages its own bottom KPI bar + safe-area spacers.
  if (/^\/deal-rooms\/[^/]+/.test(location.pathname)) return null;
  // Client-view (buyer / seller portal) is unauth, not operator shell.
  if (location.pathname.startsWith("/client-view")) return null;
  return <MobileBottomNavSpacer />;
};
